import { useEffect, useState, useMemo } from 'react';
import INavigationItem from '../../../gqlCommon/components/model/INavigationItem';
import customise from '../../constants/customise';
import { useTheme } from '@material-ui/core';
import { ISpcTheme } from '../../../common/theme/ISpcTheme';
import availableFeatureCategoriesServices from '../../service/availableFeatureCategoriesService';
import { useAvailableSections } from '../useAvailableSections';
import { useAvailableFeatures } from '../queries/useAvailableFeatures';
import useSalesEnablementContext from '../../../gqlCommon/hooks/useSalesEnablementContext';
import usePlatformConfig from '../../../common/react/usePlatformConfig';
import useNavigationBarContext from '../useNavigationBarContext';
import useCustomizeCarContext from '../useCustomizeCarContext';

const useNavigationItems = (): { navigationItems: INavigationItem[] } => {
  const {
    platformConfig: { isComplexModel, stockPage },
  } = usePlatformConfig();
  const { isInSalesEnablement, initializedWithStock } = useSalesEnablementContext();
  const { sections } = useAvailableSections();
  const { features } = useAvailableFeatures();
  const [navigationItems, setNavigationItems] = useState<INavigationItem[]>([]);
  const variantItem = customise.VARIANT_ITEM.name;
  const { selectedCar } = useCustomizeCarContext();
  const { setSelectedActiveSectionName } = useNavigationBarContext();
  const {
    spc: { customise: customiseThemeFlags },
  } = useTheme<ISpcTheme>();
  const { showVehicleStockDisplay, displayModelSection, displayVariantsSection } = customiseThemeFlags || {};
  const displayStockList = selectedCar?.similarStockCars?.length > 0;

  const defaultNavbarItems = useMemo(() => {
    if (displayStockList && stockPage && showVehicleStockDisplay) {
      return [{ name: customise.SUMMARY_ITEM.name }, { name: customise.STOCK_ITEM.name }];
    }

    return [{ name: customise.SUMMARY_ITEM.name }];
  }, [displayStockList, stockPage, showVehicleStockDisplay]);

  useEffect(() => {
    const categoriesWithFeatures = availableFeatureCategoriesServices.getCategoriesWithFeatures(
      sections,
      features,
      isInSalesEnablement,
    );
    let navbarItems: INavigationItem[] = [];
    if (categoriesWithFeatures.length) {
      navbarItems = categoriesWithFeatures.map(feature => ({
        name: feature.code,
        label: feature.label,
      }));
    }

    if (!isComplexModel || displayModelSection || displayVariantsSection) {
      if (!initializedWithStock) {
        navbarItems = [{ name: variantItem }, ...navbarItems];
      }
    }

    navbarItems = [...navbarItems, ...defaultNavbarItems];

    if (!navigationItems.length) {
      setSelectedActiveSectionName(navbarItems[0].name);
    }
    setNavigationItems(navbarItems);
  }, [
    setSelectedActiveSectionName,
    sections,
    features,
    isComplexModel,
    displayModelSection,
    displayVariantsSection,
    initializedWithStock,
    isInSalesEnablement,
  ]);

  return { navigationItems };
};

export default useNavigationItems;
